import React from "react";
import appRoutes from "../../infrastructure/constants/appRoutes";
import httpClient from "../../infrastructure/helpers/common/httpClient";
import urlHelper from "../../infrastructure/helpers/common/urlHelper";
import actions from "../../store/actions";
import GridRefresh from "../../store/state/events/GridRefresh";
import store from "../../store/store";
import BusinessAnalysisDocument from "../../types/businessAnalysis/BusinessAnalysisDocument";
import ServerDataGrid from "../Common/DataGrid/ServerDataGrid/ServerDataGrid";
import messageHelper from "../Common/Message/MessageHelper";
import { uniqueName } from "./BusinessAnalysisEditPage.Grid";
import history from '../../infrastructure/helpers/react/history';
import { getColumns } from "./BusinessAnalysisHomePage.Grid";
import identityStorage from "../../infrastructure/authorization/identityStorage";
import WarningModal from "../Common/Modals/WarningModal/WarningModal";
import RadioButtonGroup from "../Common/RadioButtonGroup/RadioButtonGroup";
import SelectOption from "../../infrastructure/types/SelectOption";

interface State {
    message: string;
    showDownloadPopup: boolean;
    documentId: number;
    showRemoveDocumentModal: boolean;
    currentDocument: BusinessAnalysisDocument;
    documents: BusinessAnalysisDocument[];
    showAddButton: boolean;
    showSpinnerForDownload: boolean;
    selectedDropdownType: DownloadType;
}

const downloadTypesOptions = [{
    text: 'Pdf',
    value: 'Pdf',
},
{
    text: 'Xlsx',
    value: 'Xlsx'
}] as SelectOption[];

enum DownloadType {
    Xlsx = 'Xlsx',
    Pdf = 'Pdf'
}

class BusinessAnalysisHomePage extends React.Component<any, State> {
    constructor(props: any) {
        super(props);

        this.state = {
            message: '',
            showRemoveDocumentModal: false,
            documentId: 0,
            showDownloadPopup: false,
            currentDocument: {} as BusinessAnalysisDocument,
            documents: [],
            showAddButton: false,
            showSpinnerForDownload: false,
            selectedDropdownType: DownloadType.Pdf
        };

        this.onEditClick = this.onEditClick.bind(this);
        this.onDownloadClick = this.onDownloadClick.bind(this);
        this.onChangeLogClick = this.onChangeLogClick.bind(this);
        this.onDocumentRenamed = this.onDocumentRenamed.bind(this);
        this.onCreateNewClick = this.onCreateNewClick.bind(this);
        this.onRecordsFetched = this.onRecordsFetched.bind(this);
        this.onDownloadConfirmed = this.onDownloadConfirmed.bind(this);
        this.onDropdownOptionChange = this.onDropdownOptionChange.bind(this);
    }

    onEditClick(id: number): void {

        let route = urlHelper.buildUrl(appRoutes.businessAnalysis.edit, { id: id });

        history.push(route);
    }

    onDownloadClick(id: number): void {
        this.setState({ documentId: id, showDownloadPopup: true });
    }

    async onDownloadConfirmed(): Promise<void> {

        this.setState({ showSpinnerForDownload: true, showDownloadPopup: false });

        try {

            let url = `business-analysis-document/download/${this.state.selectedDropdownType === DownloadType.Pdf ? 'pdf' : 'xlsx'}/${this.state.documentId}`;

            await httpClient.downloadSimple(url);
        }
        catch (error) {
            let err = error as any;

            const unauthorized = 401;

            if (err.response?.status === unauthorized) {

                identityStorage.clear();

                store.dispatch({ type: actions.navigation.execute, payload: () => history.push(appRoutes.unauthorized) });

                return Promise.reject('Server request was unauthorized');
            }

            messageHelper.showMesage(`${err.response.data.message || err.message}`);
        }
        finally {
            this.setState({ showSpinnerForDownload: false });
        }
    }

    requestGridRefresh(): void {

        let payload: GridRefresh = {
            uniqueName: uniqueName,
            useCurrentPage: true
        };

        store.dispatch({ type: actions.grid.refresh, payload: payload });
    };

    prepareDocumentOnNameChanged(document: BusinessAnalysisDocument): FormData {
        const formData = new FormData();

        formData.append("Id", document.id.toString());
        formData.append("Name", document.name || '');
        formData.append("CreatedBy", document.createdBy.toString());

        return formData;
    }

    async onDocumentRenamed(renamedDocument: BusinessAnalysisDocument, name: string) {

        if (name && this.state.documents?.[0]?.name?.trim() !== name?.trim()) {

            var form = this.prepareDocumentOnNameChanged(renamedDocument);

            await httpClient.put('business-analysis-document', form);

            messageHelper.showMesage(`Name "${name}" for document with Id: ${renamedDocument.id} was saved.`, false, true);

            let newDocument = structuredClone(this.state.currentDocument);
            newDocument.name = name;

            this.setState({ currentDocument: newDocument });
        }
    }

    async onCreateNewClick() {

        await httpClient.post('business-analysis-document/default');

        this.setState({ showAddButton: false });

        this.requestGridRefresh();
    }

    onRecordsFetched(documents: BusinessAnalysisDocument[]) {
        if (documents.length === 0) {
            this.setState({ showAddButton: true });
        }
        this.setState({ documents })
    }

    onChangeLogClick(id: number) {
        history.push(appRoutes.businessAnalysis.changeLog);
    }

    onDropdownOptionChange(type: string) {
        this.setState({ selectedDropdownType: type as DownloadType });
    }

    render(): JSX.Element {

        return (
            <>
                <h3>Business Analysis</h3>
                {this.state.showAddButton &&
                    <div className="mt-4">
                        <button type="button" className="btn cb-btn margin-bottom-10px-important" onClick={this.onCreateNewClick}>Create New Document</button>
                    </div>
                }
                <div className="mb-4">
                    <ServerDataGrid
                        columns={getColumns(
                            this.onEditClick,
                            this.onDownloadClick,
                            this.onChangeLogClick,
                            this.onDocumentRenamed,
                            this.state.showSpinnerForDownload
                        )}
                        uniqueName={uniqueName}
                        dataSourceRoute="business-analysis-document/page"
                        onRecordsFetched={this.onRecordsFetched}
                        pageSize={10}
                        getKey={x => x.id}
                        getAdditionalParams={() => this.state}
                    />
                </div>
                {
                    this.state.showDownloadPopup &&
                    <WarningModal
                        onOkClick={this.onDownloadConfirmed}
                        onCancelClick={() => this.setState({ showDownloadPopup: false })}
                        message=''
                        title='Download As' >
                        <RadioButtonGroup
                            className="margin-bottom-50px"
                            name=''
                            value={this.state.selectedDropdownType}
                            onChange={this.onDropdownOptionChange}
                            options={downloadTypesOptions}
                        />
                    </WarningModal>
                }
            </>
        );
    }
}

export default BusinessAnalysisHomePage;
